<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import { api, messageFrom, setCsrfToken } from './api'
import GenerationsPage from './pages/GenerationsPage.vue'
import LoginPage from './pages/LoginPage.vue'
import ModelsPage from './pages/ModelsPage.vue'
import PresetsPage from './pages/PresetsPage.vue'
import SummaryPage from './pages/SummaryPage.vue'
import UsersPage from './pages/UsersPage.vue'

type Tab = 'summary' | 'models' | 'presets' | 'users' | 'generations'

const loading = ref(true)
const authenticated = ref(false)
const error = ref('')
const pathname = ref(window.location.pathname)

const tab = computed<Tab>(() => {
  const match = /^\/admin\/(models|presets|users|generations|summary)\/?$/u.exec(pathname.value)
  return (match?.[1] as Tab | undefined) ?? 'summary'
})

const tabs: Array<{ code: Tab; title: string }> = [
  { code: 'summary', title: 'Сводка' },
  { code: 'models', title: 'Модели' },
  { code: 'presets', title: 'Пресеты' },
  { code: 'users', title: 'Юзеры' },
  { code: 'generations', title: 'Генерации' },
]

function navigate(next: Tab): void {
  const target = `/admin/${next}`
  if (window.location.pathname !== target) window.history.pushState({}, '', target)
  pathname.value = target
}

async function initialize(): Promise<void> {
  loading.value = true
  error.value = ''
  try {
    const session = await api<{ authenticated: boolean; csrfToken: string }>('/api/admin/auth/session')
    authenticated.value = session.authenticated
    setCsrfToken(session.csrfToken)
  } catch (reason) { error.value = messageFrom(reason) }
  finally { loading.value = false }
}

async function logout(): Promise<void> {
  try {
    const session = await api<{ csrfToken: string }>('/api/admin/auth/logout', { method: 'POST', body: '{}' })
    setCsrfToken(session.csrfToken)
  } finally { authenticated.value = false }
}

function unauthorized(): void { authenticated.value = false }
function popstate(): void { pathname.value = window.location.pathname }

onMounted(() => {
  window.addEventListener('popstate', popstate)
  window.addEventListener('kakim:unauthorized', unauthorized)
  void initialize()
})
onBeforeUnmount(() => {
  window.removeEventListener('popstate', popstate)
  window.removeEventListener('kakim:unauthorized', unauthorized)
})
</script>

<template>
  <main v-if="loading" class="boot">Открываю админку…</main>
  <main v-else-if="error" class="boot boot--error">
    <p>{{ error }}</p>
    <button class="button button--primary" type="button" @click="initialize">Повторить</button>
  </main>
  <LoginPage v-else-if="!authenticated" @logged-in="authenticated = true" />
  <div v-else class="shell">
    <header class="header">
      <div>
        <p class="eyebrow">Telegram-бот</p>
        <h1>Каким бы я был</h1>
      </div>
      <button class="button button--quiet" type="button" @click="logout">Выйти</button>
    </header>
    <nav class="tabs" aria-label="Разделы админки">
      <button
        v-for="item in tabs"
        :key="item.code"
        type="button"
        :class="['tab', { 'tab--active': tab === item.code }]"
        @click="navigate(item.code)"
      >{{ item.title }}</button>
    </nav>
    <SummaryPage v-if="tab === 'summary'" />
    <ModelsPage v-else-if="tab === 'models'" />
    <PresetsPage v-else-if="tab === 'presets'" />
    <UsersPage v-else-if="tab === 'users'" />
    <GenerationsPage v-else />
  </div>
</template>
